import { MountingPoint, Site, StayWire, Tower, TowerEquipment, TowerShape, TowerSide } from '../types/site';

export const calculateMountingPoints = (
  height: number,
  shape: TowerShape = 'square',
  spacing: number = 0.5
): MountingPoint[] => {
  const points: MountingPoint[] = [];
  let idCounter = 0;

  let sides: TowerSide[];
  let angles: number[];

  if (shape === 'triangle') {
    sides = ['A', 'B', 'C'];
    angles = [0, 120, 240];
  } else {
    sides = ['N', 'S', 'E', 'W'];
    angles = [0, 90, 180, 270];
  }

  for (let h = 0; h <= height; h += spacing) {
    sides.forEach((side, sideIndex) => {
      points.push({
        id: `mp-${idCounter++}`,
        height: h,
        side,
        angle: angles[sideIndex],
        occupied: false,
      });
    });
  }

  return points;
};

export const normDegrees = (d: number) => ((d % 360) + 360) % 360;

export const applyOrientationToMountingPoints = (
  points: MountingPoint[],
  _orientation: number
): MountingPoint[] => points;

export const applyOrientationToStayWires = (
  wires: StayWire[] = [],
  _orientation: number
): StayWire[] => wires;

export const findMountingPoint = (
  points: MountingPoint[],
  height: number,
  side: TowerSide
): MountingPoint | undefined => {
  const exact = points.find((mp) => mp.side === side && Math.abs(mp.height - height) < 1e-6);
  if (exact) return exact;
  const sameSide = points.filter((mp) => mp.side === side);
  if (sameSide.length === 0) return undefined;
  return sameSide.reduce((best, mp) =>
    Math.abs(mp.height - height) < Math.abs(best.height - height) ? mp : best
  );
};

export const resolveMountingPointId = (
  site: Site,
  mountingPointId: string
): { height: number; side: TowerSide } | null => {
  const mp = site.tower.mountingPoints.find((p) => p.id === mountingPointId);
  if (!mp) return null;
  return { height: mp.height, side: mp.side };
};

export const buildTowerWithEquipment = (
  towerBase: Omit<Tower, 'mountingPoints'> & { mountingPoints?: MountingPoint[] },
  equipmentPlacements: Array<{
    id: string;
    equipment: TowerEquipment['equipment'];
    mountingHeight: number;
    mountingSide: TowerSide;
    azimuth: number;
    elevation: number;
    label?: string;
    dishSize?: number;
    offset?: number;
    radio?: TowerEquipment['radio'];
    radioConfig?: TowerEquipment['radioConfig'];
  }>
): { tower: Tower; equipment: TowerEquipment[] } => {
  const mountingPoints = calculateMountingPoints(
    towerBase.height,
    towerBase.shape,
    0.5
  ).map((mp) => ({ ...mp }));

  const equipment: TowerEquipment[] = [];

  for (const row of equipmentPlacements) {
    const mp = findMountingPoint(mountingPoints, row.mountingHeight, row.mountingSide);
    if (!mp) continue;
    mp.occupied = true;
    equipment.push({
      id: row.id,
      equipment: row.equipment,
      mountingPointId: mp.id,
      azimuth: row.azimuth,
      elevation: row.elevation,
      label: row.label,
      dishSize: row.dishSize,
      offset: row.offset,
      radio: row.radio,
      radioConfig: row.radioConfig,
    });
  }

  const tower: Tower = {
    id: towerBase.id,
    height: towerBase.height,
    crossMemberWidth: towerBase.crossMemberWidth,
    sectionHeight: towerBase.sectionHeight,
    shape: towerBase.shape,
    stayWires: towerBase.stayWires ?? [],
    orientationDeg: towerBase.orientationDeg ?? 0,
    mountingPoints,
  };

  return { tower, equipment };
};

export const hydrateSiteTower = (site: Site): Site => {
  const placements = site.equipment.map((eq) => {
    const mp = site.tower.mountingPoints.find((p) => p.id === eq.mountingPointId);
    return {
      id: eq.id,
      equipment: eq.equipment,
      mountingHeight: mp?.height ?? 0,
      mountingSide: (mp?.side ?? 'N') as TowerSide,
      azimuth: eq.azimuth,
      elevation: eq.elevation,
      label: eq.label,
      dishSize: eq.dishSize,
      offset: eq.offset,
      radio: eq.radio,
      radioConfig: eq.radioConfig,
    };
  });

  const { tower, equipment } = buildTowerWithEquipment(
    {
      id: site.tower.id,
      height: site.tower.height,
      crossMemberWidth: site.tower.crossMemberWidth,
      sectionHeight: site.tower.sectionHeight,
      shape: site.tower.shape,
      stayWires: site.tower.stayWires ?? [],
      orientationDeg: site.tower.orientationDeg ?? 0,
    },
    placements
  );

  return { ...site, tower, equipment };
};

export const normalizeStayWires = (stayWires: StayWire[] = []): StayWire[] =>
  stayWires.map((w) => ({
    ...w,
    angle: w.angle ?? 0,
  }));
